/* global NGX */
// Core lib dependencies.
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import CryptoJS from 'crypto-js';

// Component dependencies.
import Components from './components.js';
import Actions from './actions.js';
import Reducer from './reducer.js';

// Config.
import Config from './config/config.js';
import Enums from './config/enums.js';

// Misc dependencies.
import ConfigUtil from './utils/config.js';
import { resolveBoolean } from '../../common/utils/boolean';

/**
 * <pre>
 * UgcWall.
 *
 * This app is responsible for rendering and handling all functionality relating to Ugc walls (of both social content wall AND entries gallery types).
 *
 * Summary of responsibilities:
 *
 *  - registers itself and any dependencies with NGXD
 *  - generates moduleOpts, itemOpts, templateOpts config objects for the React component props (based on supplied module config)
 *  - generates initial paging, sorting, filtering objects for the React component props (based on supplied module config)
 *  - dispatches Redux action to register initial state for each instance of UgcWall React component
 *  - renders React component to page
 * </pre>
 *
 * @author Tim Loram
 *
 * @memberof module:UgcWall
 * @namespace
 */
const App = {
  init: function() {
    // Register ourselves with NGXD.
    NGXD.registerApp(
      Config.publicName,
      Config.stateKey,
      Reducer.rootReducer,
      (function() {
        'use strict';

        // Redux _store.
        let _store;

        // The generated app config.
        const appConfig = {};

        // Any registered custom items.
        const customItems = {};

        // Registered external link handler.
        let externalLinkHandler = null;

        // The service container.
        let _serviceContainer;

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        /**
         * Init.
         *
         * @memberof module:UgcWall.App
         *
         * @param store 			{object} The Redux store.
         * @param moduleConfigs 	{array} Array of module config objects.
         * @param serviceContainer 	{Common.Services.ServiceContainer} Service container.
         */
        const init = (store, moduleConfigs, serviceContainer) => {
          // Store injected params.
          _store = store;
          _serviceContainer = serviceContainer;

          // Get global config service.
          const globalConfig = _serviceContainer.getService('config');

          // Get the Events service.
          const eventService = _serviceContainer.getService('event');

          // Loop over each modules config.
          for (const moduleId in moduleConfigs) {
            // Get the module config.
            const moduleConfig = moduleConfigs[moduleId];

            // Extract and generate config to send into the React component as props, based on module config received.
            const id = moduleId;
            const stateName = moduleConfig.stateName;
            const module = moduleConfig.module;

            // Get the app config specific to this module type (social wall/entries).
            const moduleTypeConfig = ConfigUtil.getConfigForModuleType(module.type);

            const moduleOpts = generateModuleOpts(module, moduleTypeConfig, moduleConfig.entryFilters);
            const templateOpts = generateTemplateOpts(module);
            const itemOpts = generateItemOpts(
              id,
              module,
              moduleTypeConfig,
              moduleConfig.sharingKeys,
              _serviceContainer
            );
            const paging = generatePaging(module);
            const sorting = generateSorting(module, _serviceContainer);
            const filtering = generateFiltering(module);
            const labels = generateLabels(module);
            const moduleItem = generateModuleItem(module);
            const phase = moduleConfig.phase;

            // If we are using Content Services, also set a collectionId;
            const allowContentServices = moduleTypeConfig.allowContServ;
            const contentServicesVersion = globalConfig.get('contentServices.version');
            const experienceVersion = globalConfig.get('campaign.appVersion');
            const collectionId = moduleConfig.collection || moduleOpts.collection || NGX.context.collectionId;

            // External link handler.
            const handleExternalLink = function(callback) {
              // If a custom handler has been registered, then call it.
              if (typeof externalLinkHandler === 'function') {
                externalLinkHandler(callback);
              }
              return false;
            };

            // Store all the generated config data in a config object.
            const moduleAppConfig = {
              moduleTypeConfig,
              id,
              collectionId,
              allowContentServices,
              module,
              stateName,
              moduleOpts,
              templateOpts,
              itemOpts,
              paging,
              sorting,
              filtering,
              labels,
              moduleItem,
              phase,
            };

            // Store this instances config.
            appConfig[id] = moduleAppConfig;

            // Set initial state for this instance of UGC Wall.
            dispatch(
              Actions.registerInstance(id, moduleAppConfig.moduleOpts.type, {
                paging:       moduleAppConfig.paging,
                sorting:      moduleAppConfig.sorting,
                filtering:    moduleAppConfig.filtering,
                collectionId: collectionId,
              })
            );

            //  we are only passing assetUrl and asstUgcUrl at this point as context.  Might need to add more later.
            const config = {
              assetUrl:    globalConfig.get('asset.url'),
              assetUgcUrl: globalConfig.get('asset.ugcUrl'),
            };

            // Store is passed to all child smart/container components via the ReactRedux 'Provider' convenience method.
            ReactDOM.render(
              <Provider store={_store}>
                <Components.UgcWall
                  config={config}
                  key={moduleAppConfig.id}
                  id={moduleAppConfig.id}
                  collectionId={collectionId}
                  moduleOpts={moduleAppConfig.moduleOpts}
                  templateOpts={moduleAppConfig.templateOpts}
                  itemOpts={moduleAppConfig.itemOpts}
                  paging={moduleAppConfig.paging}
                  sorting={moduleAppConfig.sorting}
                  filtering={moduleAppConfig.filtering}
                  labels={moduleAppConfig.labels}
                  moduleItem={moduleAppConfig.moduleItem}
                  allowContentServices={allowContentServices}
                  contentServicesVersion={contentServicesVersion}
                  experienceVersion={experienceVersion}
                  phase={phase}
                  externalLinkHandler={handleExternalLink}
                  triggerEvent={(event, props) => NGX.Analytics.trigger(event, props)}
                />
              </Provider>,
              document.getElementById(moduleAppConfig.id)
            );

            // Setup listener for any updates to custom items.
            eventService.listen('ugcwall:registerCustomItems', function() {
              dispatch(Actions.setCustomItems(id, moduleAppConfig.moduleTypeConfig.stateKey, getCustomItems(id)));
            });

            // Setup listener to register use of external link handler.
            eventService.listen('ugcwall:setExternalLinkHandler', function() {
              dispatch(Actions.setUseExternalLinkHandler(id, moduleAppConfig.moduleTypeConfig.stateKey, true));
            });
          }
        };

        function mapLegacyCollagePins(layouts) {
          // if ugc wall has `layouts` property (collage)
          // pins were removed from collage tile and added to pinsByTileId property of config
          // that happens if needed in collage ME, this mapping covers the case that ME is not opened since that change.
          if (!!layouts) {
            const pinnedContent = {};
            const pinsByTileId = _.reduce(
              layouts.lg,
              (acc, tile, idx) => {
                if (!_.isEmpty(tile.pinned)) {
                  const tileId = tile.i || idx;
                  acc[tileId] = _.map(tile.pinned, 'id');
                  _.forEach(tile.pinned, pin => {
                    pinnedContent[pin.id] = pin;
                  });
                }
                return acc;
              },
              {}
            );
            return { pinnedContent, pinsByTileId };
          } else {
            return {};
          }
        }

        /**
         * Generate moduleOpts object from module.
         *
         * @memberof module:UgcWall.App
         *
         * @param module {object} The module.
         * @return {object} The generated object.
         */
        const generateModuleOpts = (module, moduleTypeConfig, entryFilters) => {
          let showMoreMode = module.data.showMoreMode;
          if (!showMoreMode) {
            showMoreMode = moduleTypeConfig.showMoreMode ? moduleTypeConfig.showMoreMode : Enums.SHOW_MORE_MODE_APPEND;
          }
          const moduleOpts = {
            collection:  module.data.collection,
            animation:   module.data.animation,
            // WYN-3999 Unfortunately this value sometimes comes through as a String (based on the Store json def), and sometimes a boolean (if modified in the Module Editor),
            // so we need to cope with both types.
            autoAdvance:
              typeof module.data.autoAdvance === 'string' ? module.data.autoAdvance === 'on' : module.data.autoAdvance,
            ctaDisplayItemRender: module.data.ctaDisplayItemRender,
            type:                 module.type,
            contentType:          module.data.contentType,
            externalService:      module.data.externalService,
            displayCategory:      module.data.displayCategory,
            displayStrategy:      module.data.displayStrategy,
            scope:                module.data.scope,
            render:               module.data.render,
            target:               module.data.target,
            template:             module.data.template,
            layouts:              module.data.layouts,
            height:               module.data.fixedHeight,
            pinsByTileId:         module.data.pinsByTileId || mapLegacyCollagePins(module.data.layouts).pinsByTileId,
            pinnedContent:        module.data.pinnedContent || mapLegacyCollagePins(module.data.layouts).pinnedContent,
            shouldShowMore:
              module.data.showMore === 'on' ||
              (module.data.showMoreMode === Enums.SHOW_MORE_MODE_APPEND ||
                module.data.showMoreMode === Enums.SHOW_MORE_MODE_REPLACE),
            showMoreMode,
            autoPlayConfig: module.data.autoPlayConfig,
            shouldAutoPoll: resolveBoolean(module.data.shouldAutoPoll),
            sort:           module.data.enableSort == 'on' ? true : false,
            filter:
              module.data.enableFiltering == 'on' ||
              module.data.enableNetworkFiltering == 'on' ||
              module.data.enableUserFiltering == 'on' ||
              module.data.enableTagFiltering == 'on' ?
                true :
                false,
            filterConfig: {
              entry:   { enabled: false, displayMode: Enums.FILTER_DISPLAY_MODE_SELECT, data: {} },
              network: { enabled: false, displayMode: Enums.FILTER_DISPLAY_MODE_BUTTON, data: {} },
              tag:     { enabled: false, displayMode: Enums.FILTER_DISPLAY_MODE_BUTTON, data: {} },
              user:    { enabled: false, displayMode: Enums.FILTER_DISPLAY_MODE_TEXTINPUT, data: {} },
              type:    { enabled: false, displayMode: Enums.FILTER_DISPLAY_MODE_SELECT, data: {} },
              date:    { enabled: false, displayMode: Enums.FILTER_DISPLAY_MODE_DATE, data: {} },
            },
            shouldShowStickyContent: module.data.shouldShowStickyContent == 'on' ? true : false,
            tileRotationInterval:    module.data.tileRotationInterval,
          };

          // Enable filters based on config.
          if (module.data.enableNetworkFiltering == 'on') {
            moduleOpts.filterConfig.network.enabled = true;
          }
          if (module.data.enableUserFiltering == 'on') {
            moduleOpts.filterConfig.user.enabled = true;
          }
          if (module.data.enableTagFiltering == 'on') {
            if (module.data.filteringTags) {
              moduleOpts.filterConfig.tag.enabled = true;
              moduleOpts.filterConfig.tag.data.tags = module.data.filteringTags.split(' ');
            }
          }
          if (module.data.enableFiltering == 'on') {
            if (entryFilters) {
              moduleOpts.filterConfig.entry.enabled = true;
              moduleOpts.filterConfig.entry.data = entryFilters;
            }
          }
          return moduleOpts;
        };

        /**
         * Generate templateOpts object from module.
         *
         * @memberof module:UgcWall.App
         *
         * @param module {object} The module.
         * @return {object} The generated object.
         */
        const generateTemplateOpts = module => {
          const templateParts = module.data.template.split(':');
          const viewName = templateParts[2] ? templateParts[2] : module.type;
          const styleParts = templateParts[3].split('.');
          const moduleStyle = styleParts[0] ? styleParts[0] : 'Default';

          const templateOpts = {
            collectionType:  moduleStyle,
            itemTemplate:    styleParts[1],
            variant:         viewName === 'custom' ? null : styleParts[2],
            collectionClass: viewName,
            details:         module.data.detailTemplate ? module.data.detailTemplate : 'media',
          };

          return templateOpts;
        };

        /**
         * Generate itemOpts object from module.
         *
         * @memberof module:UgcWall.App
         *
         * @param id 				{string} The module DOM id.
         * @param module 			{object} The module.
         * @param moduleTypeConfig {object} The default module config.
         * @param sharingKeys 		{object} Sharing keys.
         * @param serviceContainer 	{Common.Services.ServiceContainer} Service container.
         * @return {object} The generated object.
         */
        const generateItemOpts = (id, module, moduleTypeConfig, sharingKeys, serviceContainer) => {
          // Get global config service.
          const globalConfig = serviceContainer.getService('config');

          const showVoting = globalConfig.get('strategies.voting') !== Enums.STRATEGY_VOTING_NONE;
          const showSocialVoting = showVoting && module.type === Enums.SOCIAL_CONTENT_WALL;
          const enableVoting = module.data.enableVoting === 'on' || moduleTypeConfig.enableVoting;

          const itemOpts = {
            moduleDomID:          id,
            ctaDisplayItemRender: module.data.ctaDisplayItemRender,

            // [DA] displayVoting and enableVoting are duplicates here, and I think displayVoting can be removed if accounted for downstream.
            // However, I didn't remove it in this hotfix lest I'd unknowingly break something.
            displayVoting: (module.data.votingPhase || showSocialVoting) && enableVoting ? true : false,
            enableVoting:  (module.data.votingPhase || showSocialVoting) && enableVoting ? true : false,
            showVoteCount: resolveBoolean(module.data.showVoteCount),
            socialVoting:  resolveBoolean(module.data.enableVoting) && showSocialVoting,

            displaySharing:        module.data.useShare !== 'nosharing' ? true : false,
            sharingMode:           module.data.useShare ? module.data.useShare : 'automatic',
            sharingStyle:          module.data.shareStyle ? module.data.shareStyle : 'icon',
            sharingKeys:           sharingKeys,
            preRollVideoAssetRef:  module.data.preRollVideoAssetRef || null,
            postRollVideoAssetRef: module.data.postRollVideoAssetRef || null,
          };

          return itemOpts;
        };

        /**
         * Generate paging object from module.
         *
         * @memberof module:UgcWall.App
         *
         * @param module {object} The module.
         * @return {object} The generated object.
         */
        const generatePaging = module => {
          const paging = {
            totalPages:  0,
            currentPage: 0,
            offset:      0,
            pageSize:    module.data.maxItems ? parseInt(module.data.maxItems) : 50,
          };

          return paging;
        };

        /**
         * Generate sorting object from module.
         *
         * @memberof module:UgcWall.App
         *
         * @param module {object} The module.
         * @param serviceContainer 	{Common.Services.ServiceContainer} Service container.
         * @return {object} The generated object.
         */
        // TODO: update this accordingly - need to mirror 'asc' or 'desc' of moderation workspace when custom is enabled
        // need to get the collection from collection id
        // need to know the collection's customOrderStrategy
        // asc or desc below based on customOrderStrategy
        const generateSorting = (module, serviceContainer) => {
          // Generate a random salt string unique to each instance.
          const randomSalt = CryptoJS.MD5(Date.now().toString()).toString();

          const sorting = {
            sortBy:         module.data.sort ? module.data.sort : 'publishDate',
            sortDir:        module.data.sort === 'custom' ? null : 'desc', // BE will determine this when sortBy is "custom" depending on customOrderStrategy of collection
            sortRandomSalt: randomSalt,
          };

          return sorting;
        };

        /**
         * Generate filtering object from module.
         *
         * @memberof module:UgcWall.App
         *
         * @param module {object} The module.
         * @return {object} The generated object.
         */
        const generateFiltering = module => {
          const filtering = {
            network: module.data.externalService,
            tag:     module.data.displayCategory,
            user:    null,
            date:    null,
            type:    null,
            entry:   {
              filter1: null,
              filter2: null,
              filter3: null,
            },
          };

          return filtering;
        };

        /**
         * Generate labels object from module.
         *
         * @memberof module:UgcWall.App
         *
         * @param module {object} The module.
         * @return {object} The generated object.
         */
        const generateLabels = module => {
          const labels = {
            // Paging related.
            paging: {
              showMore: module.data.paginationLabel,
            },
          };

          return labels;
        };

        /**
         * Generate item object from module.
         *
         * @memberof module:UgcWall.App
         *
         * @param module {object} The module.
         * @return {object} The generated object.
         */
        const generateModuleItem = module => {
          const item = {
            content: {
              heading:              module.data.heading,
              byline:               module.data.byline,
              description:          module.data.description,
              link:                 module.data.link,
              linkText:             module.data.linkText,
              featureImageAssetRef: module.data.featureImageAssetRef,
            },
          };

          return item;
        };

        /**
         * Convenience method for calling _store dispatch method. Checks _store exists first.
         *
         * @memberof module:UgcWall.App
         *
         * @param action {object} The Redux action.
         */
        const dispatch = action => {
          if (_store) {
            _store.dispatch(action);
          }
        };

        /**
         * Returns any registered custom items for the given module id.
         *
         * @memberof module:UgcWall.App
         *
         * @param moduleId {string} The module id.
         * @return {object} The custom items.
         */
        const getCustomItems = moduleId => {
          let items = {};

          if (customItems[moduleId]) {
            items = customItems[moduleId];
          }

          return items;
        };

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        // API methods.
        const api = {
          // Allow custom items to be registered against a given UgcWall instance.
          registerCustomItems: (moduleId, items) => {
            // Store items.
            customItems[moduleId] = items;

            // - - - - - - - - - - - - - - - - - -
            // Get Event service.
            const eventService = _serviceContainer.getService('event');

            // Trigger event.
            eventService.trigger('ugcwall:registerCustomItems');
          },

          // Allow collection id to be set against a given UGCWall instance.
          setCollectionId: (collectionId, moduleId = null) => {
            // - - - - - - - - - - - - - - - - - -
            // Get Event service.
            const eventService = _serviceContainer.getService('event');

            // Trigger event.
            eventService.trigger('ugcwall:setCollection', {
              moduleId:     moduleId,
              collectionId: collectionId,
            });
          },

          // Allow a custom handler function to be registered for external links.
          registerExternalLinkHandler: handler => {
            // Store the handler.
            externalLinkHandler = handler;

            // - - - - - - - - - - - - - - - - - -
            // Get Event service.
            const eventService = _serviceContainer.getService('event');

            // Trigger event.
            eventService.trigger('ugcwall:setExternalLinkHandler', {
              handler: handler,
            });
          },

          // Return app config.
          getConfig: () => {
            return appConfig;
          },
        };

        // Expose scoped properties.
        return {
          init: init,
          api:  api,
        };
      })(),
      // Array of additional reducer dependencies.
      []
    );
  },
};

export default App;
